// Landing page: census stats + assigned-patient grid + today's tasks.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Grid, Paper, Typography, TextField, InputAdornment, Stack, Chip, Divider, LinearProgress, List, ListItem, ListItemIcon, ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import HotelIcon from "@mui/icons-material/Hotel";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MedicationIcon from "@mui/icons-material/Medication";

import { useNurseData, dateFilterParams, patientMatches } from "../NurseDataContext";
import { useDebouncedValue, useMyCoverage, coveredAdmissionIds, useFulfillmentNames } from "../hooks";
import { NURSE_COLORS } from "../theme";
import { StatCard, PatientCard } from "../components";
import { getSummary, getNurseTasks, listOf } from "../api/nurseApi";
import TaskFulfillmentModal from "../components/TaskFulfillmentModal";
import MedicationAdministrationSheet from "../components/MedicationAdministrationSheet";
import { isMedicationTask } from "../medicationDisplay";
import { taskHeading, withoutDoctorNotes } from "../taskDisplay";
import { todayStr } from "../dateNav";

export default function DashboardHome() {
  const { patients, patientsLoading, navigate, currentNurseId, currentNurseName, dateFrom, dateTo } = useNurseData();
  const { myBedCodes } = useMyCoverage();
  const fulfillmentNames = useFulfillmentNames();
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null); // task being recorded

  const dateParams = useMemo(() => dateFilterParams(dateFrom, dateTo), [dateFrom, dateTo]);

  // getNurseTasks (not care_plan_tasks/) so each row carries the
  // fulfillment_form the modal renders — same source MyTasks uses.
  const reloadTasks = () => {
    getNurseTasks(dateParams)
      .then((r) => setTasks(withoutDoctorNotes(listOf(r))))
      .catch(() => setTasks([]));
  };

  useEffect(() => {
    // Summary is used only for the UNIT occupancy figure — no nurse_id filter
    // (the FE has no reliable one; passing the username mis-scoped the stats).
    getSummary(dateParams)
      .then((r) => setSummary(r?.data || null))
      .catch(() => setSummary(null));
    reloadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams]);

  // Everything below is scoped to the patients I cover this shift (roster ward
  // ∪ assigned beds) — one consistent definition of "mine".
  const myPatients = useMemo(
    () => (myBedCodes ? patients.filter((p) => p.bed && myBedCodes.has(p.bed)) : []),
    [patients, myBedCodes]
  );
  const mine = useMemo(() => coveredAdmissionIds(patients, myBedCodes), [patients, myBedCodes]);

  const debouncedQuery = useDebouncedValue(query);
  const filtered = useMemo(
    () => myPatients.filter((p) => patientMatches(p, debouncedQuery)),
    [debouncedQuery, myPatients]
  );

  const myTasks = useMemo(
    () => (mine ? tasks.filter((t) => mine.has(String(t.admission_id))) : []),
    [tasks, mine]
  );
  // nurse_dashboard_tasks/ accepts only `date` (one day) — it IGNORES the
  // from_date/to_date this view sends (nurse_dashboard_tasks.py:43-59). So the
  // fetch above is always UNBOUNDED: every task ever scheduled since admission,
  // whatever the header's range says. The cards and the per-patient badge were
  // counting that, so a patient with a fortnight of doses behind them read as
  // 21 outstanding while My Tasks (which does send `date`) showed none.
  // Windowing therefore has to happen here, for the range case too — the server
  // is not doing it. Unbounded is also what makes the backlog below countable
  // without a second request.
  const isNotDone = (t) => String(t.status).toLowerCase() !== "completed";
  const dayOf = (t) => String(t.scheduled_datetime || "").slice(0, 10);
  const hasRange = Boolean(dateFrom || dateTo);
  const today = todayStr();
  const windowStart = hasRange ? dateFrom || "0000-01-01" : today;
  const windowEnd = hasRange ? dateTo || "9999-12-31" : today;

  const windowTasks = useMemo(
    () => myTasks.filter((t) => {
      const d = dayOf(t);
      return Boolean(d) && d >= windowStart && d <= windowEnd;
    }),
    [myTasks, windowStart, windowEnd]
  );
  const pendingTasks = windowTasks.filter(isNotDone);
  const doneCount = windowTasks.length - pendingTasks.length;
  const tasksForAdmission = (admissionId) =>
    pendingTasks.filter((t) => String(t.admission_id) === String(admissionId)).length;

  // Pending work scheduled before today — genuinely missed, and exactly what
  // was inflating the counts. Excluded from the cards, named on its own instead
  // of disappearing. Zero when a range is set: the nurse chose that window.
  const backlogCount = useMemo(
    () => myTasks.filter((t) => isNotDone(t) && dayOf(t) && dayOf(t) < windowStart).length,
    [myTasks, windowStart]
  );

  // Meds due = covered medication tasks (carry a prescription_id) that are
  // scheduled at/before now and not yet recorded.
  // Plain, not memoised: `new Date()` costs nothing, and the dependency array
  // this used to carry (`tasks`) was never read inside it — it was standing in
  // for "recompute when the data refreshes", which also left `now` stale
  // between refreshes. Reading it per render is both simpler and more current.
  const now = new Date();
  const medsDue = pendingTasks.filter(
    (t) => t.prescription_id != null && t.scheduled_datetime && new Date(t.scheduled_datetime) <= now
  ).length;

  const stats = {
    patients: myPatients.length,
    pendingTasks: pendingTasks.length,
    medsDue,
    // Occupancy is a unit metric; "—" when the summary is unavailable (never a
    // misleading 0).
    occupancy: summary?.beds ? `${summary.beds.occupied}/${summary.beds.total}` : "—",
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><StatCard icon={<PeopleAltIcon />} label="My Patients" value={stats.patients} color={NURSE_COLORS.primary} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<AssignmentTurnedInIcon />} label="Pending Tasks" value={stats.pendingTasks} color={NURSE_COLORS.warning} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<MedicationIcon />} label="Meds Due" value={stats.medsDue} color={NURSE_COLORS.info} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<HotelIcon />} label="Bed Occupancy" value={stats.occupancy} color={NURSE_COLORS.success} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 2 }}>
              <Box>
                <Typography variant="subtitle1">Patients</Typography>
                <Typography variant="caption" color="text.secondary">{currentNurseName} · {myPatients.length} in your care</Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search by name, UHID, bed or bed type"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                sx={{ minWidth: { sm: 260 } }}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {patientsLoading ? (
              <LinearProgress />
            ) : (
              <Grid container spacing={1.5}>
                {filtered.map((p) => (
                  <Grid item xs={12} sm={6} key={p.id}>
                    <PatientCard patient={p} pendingTasks={tasksForAdmission(p.admissionId)} onClick={() => navigate("patient", { id: p.id })} />
                  </Grid>
                ))}
                {filtered.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <HotelIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                      <Typography variant="body2" color="text.secondary">No patients match your search.</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1">{hasRange ? "Tasks" : "Today's Tasks"}</Typography>
              <Stack direction="row" spacing={1}>
                {backlogCount > 0 && (
                  <Chip size="small" variant="outlined" color="error" label={`${backlogCount} overdue`} />
                )}
                <Chip size="small" label={`${pendingTasks.length} pending`} color="warning" />
              </Stack>
            </Stack>
            {/* Denominator is the same windowed, coverage-scoped set the
                numerator comes from — it used to divide by every task fetched,
                including other nurses' patients. */}
            <LinearProgress variant="determinate" value={windowTasks.length === 0 ? 0 : (doneCount / windowTasks.length) * 100} sx={{ mb: 1.5, height: 6, borderRadius: 1 }} />
            <List dense disablePadding>
              {pendingTasks.slice(0, 12).map((t) => {
                const key = t.id ?? `${t.care_plan_id}-${t.admission_id}`;
                return (
                  <ListItem
                    key={key}
                    disablePadding
                    onClick={() => t.id != null && setActive(t)}
                    sx={{ borderBottom: `1px solid ${NURSE_COLORS.border}`, py: 0.5, cursor: t.id != null ? "pointer" : "default" }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {/* Status glyph, not a control — the row opens the
                          recording dialog; a disabled checkbox reads as broken. */}
                      <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{t.compact_detail || taskHeading(t, fulfillmentNames)}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">Admission {t.admission_id} · {(t.scheduled_datetime || "").replace("T", " ").slice(0, 16)}</Typography>}
                    />
                    {t.prescription_received === false && (
                      <Chip size="small" color="warning" label="meds not received" />
                    )}
                  </ListItem>
                );
              })}
              {pendingTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No pending tasks.</Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Medication doses go through the administration sheet wherever they are
          tapped; other nursing work keeps the generic fulfillment form. */}
      {isMedicationTask(active) ? (
        <MedicationAdministrationSheet
          open={!!active}
          task={active}
          performedById={currentNurseId}
          onClose={() => setActive(null)}
          onRecorded={reloadTasks}
        />
      ) : (
        <TaskFulfillmentModal
          open={!!active}
          task={active}
          performedById={currentNurseId}
          onClose={() => setActive(null)}
          onFulfilled={reloadTasks}
        />
      )}
    </Box>
  );
}
